import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { defineMessages } from 'react-intl';

import { postCommandFileImportSampleMutation } from '../api/generated-evs/@tanstack/react-query.gen';
import { createTranslatedError, ErrorTranslations } from '../errors/transtatedErrors';

const fileErrorTranslations: ErrorTranslations = defineMessages({
  'invalid-format': {
    id: 'error.invalid-format',
    defaultMessage: 'Tenhle soubor si s námi nerozumí. Přijímáme jen formáty .csv a .xlsx. Zkuste to znovu.',
  },
  'file-parse-error': {
    id: 'error.file-parse-error',
    defaultMessage: 'Chyba při zpracování souboru',
  },
  'file-empty': {
    id: 'error.file-empty',
    defaultMessage: 'Soubor je prázdný',
  },
  'missing-columns': {
    id: 'error.missing-columns',
    defaultMessage: 'V souboru chybí sloupce: {columns}',
  },
  'missing-variable-columns': {
    id: 'error.missing-variable-columns',
    defaultMessage: 'V souboru chybí alespoň jeden z těchto sloupců: {columns}',
  },
  'ipsos-id-empty': {
    id: 'error.ipsos-id-empty',
    defaultMessage: 'IpsosId je v jednom z řádků prázdný',
  },
  'duplicate-ipsos-ids': {
    id: 'error.duplicate-ipsos-ids',
    defaultMessage: 'V souboru jsou duplicitní IpsosId: {duplicateIds}',
  },
  'all-phone-numbers-empty-in-row': {
    id: 'error.all-phone-numbers-empty-in-row',
    defaultMessage:
      'Každý řádek musí mít alespoň jedno neprázdné telefonní číslo. IpsosId řádků bez telefonního čísla: {invalidIpsosIds}',
  },
  'invalid-headers': {
    id: 'error.invalid-headers',
    defaultMessage: 'V souboru jsou nepovolené znaky v názvech sloupců: {invalidHeaders}',
  },
  'conflicting-headers': {
    id: 'error.conflicting-headers',
    defaultMessage:
      'Jejda — některé názvy sloupců ve souboru nejsou úplně stejné jako v projektu (záleží i na velkých a malých písmenech). Přejmenujte je podle dvojic (ve souboru → správně): {conflictingHeaders}',
  },
  'duplicate-headers': {
    id: 'error.duplicate-headers',
    defaultMessage: 'V souboru jsou duplicitní názvy sloupců (název - pozice): {duplicateHeaders}',
  },
  'ipsos-id-not-numeric': {
    id: 'error.ipsos-id-not-numeric',
    defaultMessage: 'IpsosId musí být číslo. Nesprávné IpsosId: {invalidIds}',
  },
  'banned-import-headers': {
    id: 'error.banned-import-headers',
    defaultMessage: 'V souboru jsou sloupce, které nelze importovat: {bannedImportHeaders}',
  },
  default: {
    id: 'error.default',
    defaultMessage: 'Něco se pokazilo. Zkuste to znovu.',
  },
});

export type FileImportApiCall = (
  file: File,
  database: string,
  prefillColumns?: Record<string, string>,
  parking?: string
) => Promise<{ operationId?: string }>;

export function useFileImportApiCall(qreId: string): FileImportApiCall {
  const uploadSampleMutation = useMutation(postCommandFileImportSampleMutation());

  return useCallback(
    async (file: File, database: string, prefillColumns?: Record<string, string>, parking?: string) => {
      try {
        const body: {
          qreId: string;
          file: File;
          database: string;
          prefillColumns?: Record<string, string>;
          parking: string;
        } = {
          qreId,
          file,
          database,
          parking: parking ?? '0',
        };

        if (prefillColumns && Object.keys(prefillColumns).length > 0) {
          body.prefillColumns = prefillColumns;
        }

        const response = await uploadSampleMutation.mutateAsync({
          body,
        });

        return response as { operationId?: string };
      } catch (error) {
        // @ts-expect-error type
        throw createTranslatedError(error, fileErrorTranslations);
      }
    },
    [uploadSampleMutation, qreId]
  );
}
